// src/services/audio-notification.service.ts

/**
 * Service for handling audio notifications in the application
 * Provides methods for playing different types of notification sounds
 */
export class AudioNotificationService {
    /**
     * Plays a standard notification sound (two-tone chime)
     * @param soundType Type of sound to play ('standard' | 'gentle' | 'success')
     */
    static playNotification(soundType: 'standard' | 'gentle' | 'success' = 'standard'): void {
        try {
            switch (soundType) {
                case 'gentle':
                    AudioNotificationService.playGentleSound();
                    break;
                case 'success':
                    AudioNotificationService.playSuccessSound();
                    break;
                default:
                    AudioNotificationService.playNotificationSound();
                    break;
            }
        } catch (error) {
            console.error("Audio notification failed:", error);
        }
    }

    /**
     * Plays a distinctive two-tone notification sound
     * Uses Web Audio API for better sound quality and control
     */
    private static playNotificationSound(): void {
        try {
            // Create audio context
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            const audioCtx = new AudioContext();

            // Play first tone (C6)
            AudioNotificationService.playTone(audioCtx, 1046.50, 0.3, 0.5);

            // Play second tone (E6) after a short delay
            setTimeout(() => {
                AudioNotificationService.playTone(audioCtx, 1318.51, 0.3, 0.5);
            }, 150);
        } catch (error) {
            console.error("Sound playback failed:", error);
        }
    }

    /**
     * Helper method to play a single tone with the Web Audio API
     * @param audioCtx The audio context
     * @param frequency Frequency of the tone in Hz
     * @param maxGain Maximum gain (volume) of the tone
     * @param duration Duration of the tone in seconds
     * @param startTime Optional start time (defaults to current time)
     */
    private static playTone(
        audioCtx: AudioContext,
        frequency: number,
        maxGain: number,
        duration: number,
        startTime?: number
    ): void {
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        const playTime = startTime || audioCtx.currentTime;

        // Configure oscillator
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, playTime);

        // Configure volume envelope
        gainNode.gain.setValueAtTime(0, playTime);
        gainNode.gain.linearRampToValueAtTime(maxGain, playTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, playTime + duration);

        // Connect nodes
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        // Play sound
        oscillator.start(playTime);
        oscillator.stop(playTime + duration);
    }

    /**
     * Plays a gentle "tada" or ringing sound
     * Uses a pleasant major chord progression for a welcoming feel
     */
    private static playGentleSound(): void {
        try {
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            const audioCtx = new AudioContext();

            // Play a gentle major chord progression (C-E-G)
            const frequencies = [261.63, 329.63, 392.00]; // C4, E4, G4
            const startTime = audioCtx.currentTime;

            frequencies.forEach((freq, index) => {
                // Stagger the notes slightly for a more musical effect
                const noteStartTime = startTime + (index * 0.1);
                AudioNotificationService.playTone(audioCtx, freq, 0.3, 0.8, noteStartTime);
            });
        } catch (error) {
            console.error("Gentle sound playback failed:", error);
        }
    }

    /**
     * Plays a success sound (uplifting chime)
     * Uses an ascending major scale for a positive feel
     */
    private static playSuccessSound(): void {
        try {
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            const audioCtx = new AudioContext();

            // Play an ascending C major scale (C-D-E-F-G)
            const frequencies = [261.63, 293.66, 329.63, 349.23, 392.00];
            const startTime = audioCtx.currentTime;

            frequencies.forEach((freq, index) => {
                const noteStartTime = startTime + (index * 0.15);
                AudioNotificationService.playTone(audioCtx, freq, 0.4, 0.4, noteStartTime);
            });
        } catch (error) {
            console.error("Success sound playback failed:", error);
        }
    }
}
